import { createAsyncThunk } from '@reduxjs/toolkit'
import { ProjectResourceController } from 'controllers/Project/ProjectResourceController'
import {
  PriceSummaryDto,
  QuantityDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { getPriceSummaryKey } from 'store/Project/ProjectStateUtils'
import { projectAPIBuilder } from './BoMAsyncActions'
import { fetchProject } from './fetchProject'

const SaveCostDriverDiscountRatio = createAsyncThunk<
  Promise<void>,
  { newRatio: QuantityDto; priceSummary: PriceSummaryDto },
  { state: RootAppState }
>(
  'priceSummary/SaveCostDriverDiscountRatio',
  async ({ newRatio, priceSummary }, thunkAPI) => {
    const { api, projectId } = projectAPIBuilder(thunkAPI.getState)

    try {
      if (priceSummary?.isMaterial) {
        return await api.SetArticleDiscountRatio(
          projectId,
          priceSummary.materialId,
          {
            ...priceSummary.discountRatio,
            value: newRatio.value,
          }
        )
      } else if (priceSummary?.isWorkingStep) {
        const controller = new ProjectResourceController(
          priceSummary.workingStep.resource.id,
          getPriceSummaryKey(priceSummary)
        )

        return await controller.SetResourceDiscount({
          discountRatio: newRatio,
        })
      }
    } catch (err) {
      ShowException('project', err)
      thunkAPI.dispatch(fetchProject({ projectId: projectId }))
      throw err
    }
  }
)

const SaveCostDriverSurchargeRatio = createAsyncThunk<
  void,
  { newRatio: QuantityDto; priceSummary: PriceSummaryDto },
  { state: RootAppState }
>(
  'priceSummary/SaveCostDriverSurchargeRatio',
  async ({ newRatio, priceSummary }, thunkAPI) => {
    const { api, projectId } = projectAPIBuilder(thunkAPI.getState)

    try {
      if (priceSummary?.isMaterial) {
        return await api.SetArticleSurchargeRatio(
          projectId,
          priceSummary.materialId,
          {
            ...priceSummary.surchargeRatio,
            value: newRatio.value,
          }
        )
      } else if (priceSummary?.isWorkingStep) {
        const resourceController = new ProjectResourceController(
          priceSummary.workingStep.resource.id,
          getPriceSummaryKey(priceSummary)
        )

        return await resourceController.SetResourceSurcharge({
          surchargeRatio: newRatio,
        })
      }
    } catch (err) {
      ShowException('project', err)
      thunkAPI.dispatch(fetchProject({ projectId: projectId }))
      throw err
    }
  }
)

const ResetCostDriverDiscountToDefault = createAsyncThunk<
  void,
  { priceSummary: PriceSummaryDto },
  { state: RootAppState }
>(
  'priceSummary/resetCostDriverDiscountToDefault',
  async ({ priceSummary }, thunkAPI) => {
    const { api, projectId } = projectAPIBuilder(thunkAPI.getState)

    try {
      if (priceSummary?.isMaterial) {
        return await api.DeleteArticleDiscount(
          projectId,
          priceSummary.materialId
        )
      } else if (priceSummary?.isWorkingStep) {
        const controller = new ProjectResourceController(
          priceSummary.workingStep.resource.id,
          getPriceSummaryKey(priceSummary)
        )

        return await controller.DeleteResourceDiscount()
      }
    } catch (err) {
      ShowException('project', err)
      thunkAPI.dispatch(fetchProject({ projectId: projectId }))
      throw err
    }
  }
)

const ResetCostDriverSurchargeToDefault = createAsyncThunk<
  void,
  { priceSummary: PriceSummaryDto },
  { state: RootAppState }
>(
  'priceSummary/ResetCostDriverSurchargeToDefault',
  async ({ priceSummary }: { priceSummary: PriceSummaryDto }, thunkAPI) => {
    const { api, projectId } = projectAPIBuilder(thunkAPI.getState)

    try {
      if (priceSummary?.isMaterial) {
        return await api.DeleteArticleSurcharge(
          projectId,
          priceSummary.materialId
        )
      } else if (priceSummary?.isWorkingStep) {
        const controller = new ProjectResourceController(
          priceSummary.workingStep.resource.id,
          getPriceSummaryKey(priceSummary)
        )

        return await controller.DeleteResourceSurcharge()
      }
    } catch (err) {
      ShowException('project', err)
      thunkAPI.dispatch(fetchProject({ projectId: projectId }))
      throw err
    }
  }
)

export const CostDriversActions = {
  SaveCostDriverDiscountRatio,
  ResetCostDriverDiscountToDefault,
  SaveCostDriverSurchargeRatio,
  ResetCostDriverSurchargeToDefault,
}
